import { AlertCircle, RefreshCw } from "lucide-react";
import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from "react";
import {
  Drawer,
  Button,
  LoadingOverlay,
  Alert,
  Text,
  Group,
  Stack,
} from "@mantine/core";
import { useFilterOptionsApiService } from "../service/api/InboxQueryApiService";
import { InboxFilterState } from "../utils/types";
import CategoryCheckbox from "./CategoryCheckbox";
import TypeMultiSelect from "./TypeMultiSelect";
import { AuthContext } from "../../../providers/AuthProvider";
import UserAccessControlUtils, {
  FEATURE_LEAVE_MANAGEMENT,
  FEATURE_PAYSLIP_QUERY,
  FEATURE_TIMESHEET_QUERY,
  FEATURE_ROSTER_QUERY,
} from "../../../utils/UserAccessControlUtils";

interface InboxFilterDrawerProps {
  opened: boolean;
  onClose: () => void;
  onApplyFilters: (filters: InboxFilterState) => void;
  initialFilters?: InboxFilterState;
}

const InboxFilterDrawer: React.FC<InboxFilterDrawerProps> = ({
  opened,
  onClose,
  onApplyFilters,
  initialFilters,
}) => {
  const {
    data: filterOptions,
    isLoading,
    error,
    refetch,
  } = useFilterOptionsApiService();
  const auth = useContext(AuthContext);

  // Check if leave management feature is available
  const hasLeaveFeature = UserAccessControlUtils.hasFeature(
    auth?.user_features ?? [],
    FEATURE_LEAVE_MANAGEMENT
  );

  const hasPayslipQueryFeature = UserAccessControlUtils.hasFeature(
    auth?.user_features ?? [],
    FEATURE_PAYSLIP_QUERY
  );

  const hasRosterQueryFeature = UserAccessControlUtils.hasFeature(
    auth?.user_features ?? [],
    FEATURE_ROSTER_QUERY
  );

  const hasTimesheetQueryFeature = UserAccessControlUtils.hasFeature(
    auth?.user_features ?? [],
    FEATURE_TIMESHEET_QUERY
  );

  const hasAtLeastOneQueryFeature =
    hasPayslipQueryFeature || hasRosterQueryFeature || hasTimesheetQueryFeature;

  // Get all available leave status options - memoized to prevent recreation
  const getAllLeaveStatuses = useMemo((): Array<{
    value: string;
    display_name: string;
  }> => {
    // Return empty array if no filterOptions or in error state
    if (!filterOptions?.leave_statuses || error) return [];

    try {
      // Return all available leave statuses without tab-based filtering
      return filterOptions.leave_statuses;
    } catch {
      return [];
    }
  }, [filterOptions, error]);

  // Filter state - initialize from props or defaults
  const [queriesEnabled, setQueriesEnabled] = useState(
    initialFilters?.queriesEnabled ?? hasAtLeastOneQueryFeature
  );
  const [leaveRequestsEnabled, setLeaveRequestsEnabled] = useState(
    initialFilters?.leaveRequestsEnabled ?? hasLeaveFeature
  );
  const [selectedQueryTypes, setSelectedQueryTypes] = useState<string[]>(
    initialFilters?.selectedQueryTypes ?? []
  );
  const [selectedLeaveTypes, setSelectedLeaveTypes] = useState<string[]>(
    initialFilters?.selectedLeaveTypes ?? []
  );
  const [selectedLeaveStatuses, setSelectedLeaveStatuses] = useState<string[]>(
    initialFilters?.selectedLeaveStatuses ?? []
  );

  // Sync internal state with initialFilters prop changes
  useEffect(() => {
    if (initialFilters) {
      setQueriesEnabled(
        initialFilters.queriesEnabled ?? hasAtLeastOneQueryFeature
      );
      setLeaveRequestsEnabled(
        initialFilters.leaveRequestsEnabled ?? hasLeaveFeature
      );
      setSelectedQueryTypes(initialFilters.selectedQueryTypes ?? []);
      setSelectedLeaveTypes(initialFilters.selectedLeaveTypes ?? []);
      setSelectedLeaveStatuses(initialFilters.selectedLeaveStatuses ?? []);
    }
  }, [
    initialFilters?.queriesEnabled,
    initialFilters?.leaveRequestsEnabled,
    JSON.stringify(initialFilters?.selectedQueryTypes ?? []),
    JSON.stringify(initialFilters?.selectedLeaveTypes ?? []),
    JSON.stringify(initialFilters?.selectedLeaveStatuses ?? []),
    hasLeaveFeature,
    hasAtLeastOneQueryFeature,
  ]); // Use stable primitive dependencies instead of object reference

  // Clean up invalid leave status selections when tab changes
  useEffect(() => {
    // Only run if filterOptions is available and not in error state
    if (!error && filterOptions?.leave_statuses) {
      const validStatusValues = getAllLeaveStatuses.map(
        (status) => status.value
      );

      setSelectedLeaveStatuses((prevStatuses) => {
        const currentlySelected = prevStatuses.filter((status) =>
          validStatusValues.includes(status)
        );

        // Only return new array if there's actually a change
        return currentlySelected.length !== prevStatuses.length
          ? currentlySelected
          : prevStatuses;
      });
    }
  }, [filterOptions, error, getAllLeaveStatuses]); // Added getAllLeaveStatuses to dependencies

  // Memoized helper function to validate and update query types
  const validateQueryTypes = useCallback((validQueryTypes: string[]): void => {
    setSelectedQueryTypes((prevTypes) => {
      const validSelectedQueryTypes = prevTypes.filter((type) =>
        validQueryTypes.includes(type)
      );
      return validSelectedQueryTypes.length !== prevTypes.length
        ? validSelectedQueryTypes
        : prevTypes;
    });
  }, []);

  // Memoized helper function to validate and update leave types
  const validateLeaveTypes = useCallback((validLeaveTypes: string[]): void => {
    setSelectedLeaveTypes((prevTypes) => {
      const validSelectedLeaveTypes = prevTypes.filter((type) =>
        validLeaveTypes.includes(type)
      );
      return validSelectedLeaveTypes.length !== prevTypes.length
        ? validSelectedLeaveTypes
        : prevTypes;
    });
  }, []);

  // Memoized helper function to validate and update leave statuses
  const validateLeaveStatuses = useCallback((): void => {
    const validStatusValues = getAllLeaveStatuses.map((status) => status.value);

    setSelectedLeaveStatuses((prevStatuses) => {
      const validSelectedStatuses = prevStatuses.filter((status) =>
        validStatusValues.includes(status)
      );
      return validSelectedStatuses.length !== prevStatuses.length
        ? validSelectedStatuses
        : prevStatuses;
    });
  }, [getAllLeaveStatuses]);

  // Clean up invalid selections when filter options change (e.g., after refresh)
  useEffect(() => {
    if (!error && filterOptions) {
      // Validate query types
      if (filterOptions.query_types) {
        const validQueryTypes = filterOptions.query_types.map((qt) => qt.value);
        validateQueryTypes(validQueryTypes);
      }

      // Validate leave types
      if (filterOptions.leave_types) {
        const validLeaveTypes = filterOptions.leave_types.map((lt) => lt.value);
        validateLeaveTypes(validLeaveTypes);
      }

      // Validate leave statuses (also considering tab filtering)
      if (filterOptions.leave_statuses) {
        validateLeaveStatuses();
      }
    }
  }, [
    filterOptions,
    error,
    validateQueryTypes,
    validateLeaveTypes,
    validateLeaveStatuses,
  ]); // Removed state variables from dependencies and added memoized validation functions

  const handleClearAll = (): void => {
    setQueriesEnabled(hasAtLeastOneQueryFeature);
    setLeaveRequestsEnabled(hasLeaveFeature);
    setSelectedQueryTypes([]);
    setSelectedLeaveTypes([]);
    setSelectedLeaveStatuses([]);
  };

  // Handle Document queries checkbox change
  const handleQueriesChange = (checked: boolean): void => {
    setQueriesEnabled(checked);

    if (!checked) {
      // Clear all query types when unchecking queries
      setSelectedQueryTypes([]);
    }
  };

  // Handle Leave requests checkbox change
  const handleLeaveRequestsChange = (checked: boolean): void => {
    setLeaveRequestsEnabled(checked);

    if (!checked) {
      // Clear all leave types and statuses when unchecking leave requests
      setSelectedLeaveTypes([]);
      setSelectedLeaveStatuses([]);
    }
  };

  // Validation logic for Show Results button
  const isShowResultsDisabled = (): boolean => {
    try {
      // Disable button if in error state or loading
      if (error || isLoading) return true;

      // If leave feature is not available, only check if queries are enabled
      if (!hasLeaveFeature) {
        return !queriesEnabled;
      }
      // If leave feature is available, require at least one category to be enabled
      return !queriesEnabled && !leaveRequestsEnabled;
    } catch {
      // If there's any error in validation, disable the button
      return true;
    }
  };

  // Handle Show Results button click
  const handleApplyFilters = (): void => {
    const filterState: InboxFilterState = {
      queriesEnabled,
      leaveRequestsEnabled,
      selectedQueryTypes: selectedQueryTypes || [],
      selectedLeaveTypes: selectedLeaveTypes || [],
      selectedLeaveStatuses: selectedLeaveStatuses || [],
    };

    onApplyFilters(filterState);
    onClose();
  };

  // Helper function to render error state
  const renderErrorState = (): JSX.Element => (
    <div role="alert" aria-live="polite">
      <Alert
        icon={<AlertCircle size={16} />}
        color="red"
        radius="md"
        style={{ marginBottom: "16px" }}
      >
        <Text size="sm" weight={500}>
          Failed to load filter options
        </Text>
        <Text size="xs" color="dimmed" style={{ marginTop: "4px" }}>
          Please check your connection and try again
        </Text>
      </Alert>
      <Group position="center">
        <Button
          leftIcon={<RefreshCw size={16} />}
          variant="outline"
          color="gray"
          onClick={() => refetch()}
          fullWidth
        >
          Retry
        </Button>
      </Group>
    </div>
  );

  // Helper function to render empty state
  const renderEmptyState = (): JSX.Element => (
    <div role="alert" aria-live="polite">
      <Stack align="center" spacing="md" style={{ padding: "32px 16px" }}>
        <Text size="md" weight={500} color="dimmed" align="center">
          No filter options available
        </Text>
        <Text size="sm" color="dimmed" align="center">
          Filter options might not be configured yet
        </Text>
        <Button
          leftIcon={<RefreshCw size={16} />}
          variant="outline"
          color="gray"
          onClick={() => refetch()}
        >
          Refresh
        </Button>
      </Stack>
    </div>
  );

  // Helper function to check if we should show empty state
  const shouldShowEmptyState = (): boolean => {
    if (!filterOptions) return false;

    const hasQueryTypes =
      filterOptions.query_types && filterOptions.query_types.length > 0;
    const hasLeaveTypes =
      filterOptions.leave_types && filterOptions.leave_types.length > 0;
    const hasLeaveStatuses =
      filterOptions.leave_statuses && filterOptions.leave_statuses.length > 0;

    return !hasQueryTypes && !hasLeaveTypes && !hasLeaveStatuses;
  };

  // Helper function to check if we should show normal content
  const shouldShowContent = (): boolean => {
    if (!filterOptions) return false;

    const hasQueryTypes =
      filterOptions.query_types && filterOptions.query_types.length > 0;
    const hasLeaveTypes =
      filterOptions.leave_types && filterOptions.leave_types.length > 0;
    const hasLeaveStatuses =
      filterOptions.leave_statuses && filterOptions.leave_statuses.length > 0;

    return hasQueryTypes || hasLeaveTypes || hasLeaveStatuses;
  };

  // Helper function to render filter content
  const renderFilterContent = (): JSX.Element => (
    <>
      {/* Queries Section */}
      {hasAtLeastOneQueryFeature && (
        <div style={{ marginBottom: "24px" }}>
          <CategoryCheckbox
            label="Queries"
            checked={queriesEnabled}
            onChange={handleQueriesChange}
          />

          {queriesEnabled &&
            filterOptions?.query_types &&
            filterOptions.query_types.length > 0 && (
              <TypeMultiSelect
                label="Type"
                data={filterOptions.query_types}
                value={selectedQueryTypes}
                onChange={setSelectedQueryTypes}
                placeholder="Select query types"
              />
            )}
        </div>
      )}

      {/* Leave Section - only show if leave feature is available */}
      {hasLeaveFeature && (
        <div style={{ marginBottom: "24px" }}>
          <CategoryCheckbox
            label="Leave"
            checked={leaveRequestsEnabled}
            onChange={handleLeaveRequestsChange}
          />

          {leaveRequestsEnabled && (
            <>
              {filterOptions?.leave_types &&
                filterOptions.leave_types.length > 0 && (
                  <TypeMultiSelect
                    label="Type"
                    data={filterOptions.leave_types}
                    value={selectedLeaveTypes}
                    onChange={setSelectedLeaveTypes}
                    placeholder="Select leave types"
                  />
                )}

              {filterOptions?.leave_statuses &&
                filterOptions.leave_statuses.length > 0 &&
                getAllLeaveStatuses.length > 0 && (
                  <TypeMultiSelect
                    label="Status"
                    data={getAllLeaveStatuses}
                    value={selectedLeaveStatuses}
                    onChange={setSelectedLeaveStatuses}
                    placeholder="Select status"
                  />
                )}
            </>
          )}
        </div>
      )}
    </>
  );

  // Helper function to render action buttons
  const renderActionButtons = (): JSX.Element => (
    <div
      style={{
        position: "sticky",
        bottom: 0,
        backgroundColor: "white",
        padding: "16px 0",
        marginTop: "auto",
      }}
    >
      <Button
        fullWidth
        color="jem-theme"
        size="md"
        style={{ marginBottom: "12px" }}
        disabled={isShowResultsDisabled()}
        onClick={handleApplyFilters}
      >
        Show Results
      </Button>
      <Button
        fullWidth
        variant="outline"
        color="gray"
        size="md"
        onClick={handleClearAll}
      >
        Clear All
      </Button>
    </div>
  );

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="md"
      title="Filter"
      overlayOpacity={0.1}
      overlayBlur={3}
    >
      <div
        style={{
          padding: "16px",
          height: "calc(100vh - 120px)",
          overflow: "auto",
        }}
      >
        <LoadingOverlay visible={isLoading} />

        {/* Error State */}
        {error && !isLoading && renderErrorState()}

        {/* Empty State */}
        {!error && !isLoading && shouldShowEmptyState() && renderEmptyState()}

        {/* Normal Content */}
        {!error && !isLoading && shouldShowContent() && (
          <>
            {renderFilterContent()}
            {renderActionButtons()}
          </>
        )}
      </div>
    </Drawer>
  );
};

export default InboxFilterDrawer;
